import { COPY } from '@/features/product-settings/copy';
import { formatDate } from '@/lib/format';

type Audited = {
    createdOn: string;
    createdBy: string | null;
    revisedOn: string | null;
    revisedBy: string | null;
};

/** Who created and last revised the row, as the parts of a drawer's description line. */
export function auditParts(row: Audited): string[] {
    return [
        COPY.audit.createdBy(row.createdBy, formatDate(row.createdOn)),
        ...(row.revisedOn ? [COPY.audit.revisedBy(row.revisedBy, formatDate(row.revisedOn))] : []),
    ];
}

/** One line under a drawer's title: state, then who created and last revised the row. */
export function auditLine(status: string, row: Audited) {
    return [status, ...auditParts(row)].filter(Boolean).join(' · ');
}

/** The legacy OrderNo: a whole number, zero or more, kept as text while typed. */
export function parseOrderNo(text: string): number | null {
    const trimmed = text.trim();
    if (trimmed === '') return 0;
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** "62044900" shown as the customs paperwork writes it: "6204.49.00", with a fourth pair when there is one. */
export function formatCommodityCode(digits: string): string {
    const clean = digits.replace(/\D/g, '');
    if (clean.length <= 4) return clean;
    return [clean.slice(0, 4), ...(clean.slice(4).match(/.{1,2}/g) ?? [])].join('.');
}

/** The digits alone, so a search or a save treats "6204.49.00", "6204 49 00" and "62044900" as one code. */
export function normaliseCommodityCode(text: string): string {
    return text.replace(/[\s.-]/g, '');
}

/** Well formed as the library stores it: six to ten digits. */
export function isCommodityCode(text: string): boolean {
    return /^\d{6,10}$/.test(normaliseCommodityCode(text));
}
